import { useEffect, useRef } from 'react';
import { NavigationType, useLocation, useNavigationType } from 'react-router-dom';

import { getLastPublishedPageTitle, subscribePageTitle } from '@/shared/lib/analytics/page-title-registry';
import { createPageViewScheduler, type PageViewScheduler } from '@/shared/lib/analytics/page-view-scheduler';

import { logPageView } from './firebase';

/**
 * 라우트 변경마다 page_view 1건.
 * TRAD-3701 — 종전엔 위치 변경 effect 에서 곧바로 `document.title` 을 보냈는데, PageMeta(Helmet)가 제목을 그 «뒤»에
 * 바꿔 SPA 이동마다 이전 화면 제목·URL 이 찍혔다. 이제 PageMeta 가 알린 «이 경로의 제목»을 기다렸다 보낸다
 * (규칙은 page-view-scheduler.ts).
 */
export const FirebaseAnalyticsTracker = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const schedulerRef = useRef<PageViewScheduler | null>(null);
  if (schedulerRef.current === null) {
    schedulerRef.current = createPageViewScheduler({
      send: ({ path, title, location: pageLocation }) => logPageView(path, title, pageLocation),
      setTimer: (fn, ms) => setTimeout(fn, ms),
      clearTimer: handle => clearTimeout(handle as ReturnType<typeof setTimeout>),
      getLastPublished: getLastPublishedPageTitle,
    });
  }

  // 구독이 navigate 보다 먼저 걸리도록 effect 순서를 지킨다(같은 컴포넌트 안 effect 는 선언 순서로 돈다).
  useEffect(() => subscribePageTitle(({ path, title }) => schedulerRef.current?.titlePublished(path, title)), []);

  useEffect(() => {
    const path = location.pathname + location.search;
    const scheduler = schedulerRef.current;
    // 리다이렉트(/news → /news?category=CRYPTO)가 page_view 2건이 되지 않게 — 규칙은 page-view-scheduler.ts.
    if (navigationType === NavigationType.Replace) {
      scheduler?.replace(path, window.location.origin + path);
    } else {
      scheduler?.navigate(path, window.location.origin + path);
    }
  }, [location, navigationType]);

  useEffect(() => () => schedulerRef.current?.flush(), []);

  return null;
};
